import { pixelsToInches } from './measurements.js';

export function makeId(prefix = 'id') {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

export function distanceBetweenPoints(a, b) {
  if (!a || !b) return 0;
  return Math.hypot((b.x ?? 0) - (a.x ?? 0), (b.y ?? 0) - (a.y ?? 0));
}

export function polygonArea(points = []) {
  if (points.length < 3) return 0;
  let area = 0;
  for (let i = 0; i < points.length; i += 1) {
    const current = points[i];
    const next = points[(i + 1) % points.length];
    area += current.x * next.y - next.x * current.y;
  }
  return Math.abs(area / 2);
}

export function signedPolygonArea(points = []) {
  if (points.length < 3) return 0;
  let area = 0;
  for (let i = 0; i < points.length; i += 1) {
    const current = points[i];
    const next = points[(i + 1) % points.length];
    area += current.x * next.y - next.x * current.y;
  }
  return area / 2;
}

export function polygonCentroid(points = []) {
  if (!points.length) return { x: 0, y: 0 };
  const area = signedPolygonArea(points);
  if (Math.abs(area) < 0.001) {
    return points.reduce((acc, p) => ({ x: acc.x + p.x / points.length, y: acc.y + p.y / points.length }), { x: 0, y: 0 });
  }
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < points.length; i += 1) {
    const current = points[i];
    const next = points[(i + 1) % points.length];
    const factor = current.x * next.y - next.x * current.y;
    cx += (current.x + next.x) * factor;
    cy += (current.y + next.y) * factor;
  }
  return { x: cx / (6 * area), y: cy / (6 * area) };
}

export function flattenPoints(points = []) {
  return points.flatMap((point) => [point.x, point.y]);
}

export function unflattenPoints(values = []) {
  const points = [];
  for (let i = 0; i < values.length; i += 2) {
    points.push({ x: values[i], y: values[i + 1] });
  }
  return points;
}

export function segmentAngle(a, b) {
  const angle = Math.atan2((b?.y ?? 0) - (a?.y ?? 0), (b?.x ?? 0) - (a?.x ?? 0));
  return ((angle * 180) / Math.PI + 360) % 360;
}

export function snapToPolarAngle(start, end, allowedAngles = [0, 45, 90, 135, 180, 225, 270, 315], toleranceDegrees = 8) {
  if (!start || !end) return end;
  const distance = distanceBetweenPoints(start, end);
  if (distance < 1) return end;
  const rawAngle = segmentAngle(start, end);
  let nearest = allowedAngles[0];
  let delta = 360;
  allowedAngles.forEach((angle) => {
    const diff = Math.abs((((rawAngle - angle + 540) % 360) - 180));
    if (diff < delta) {
      delta = diff;
      nearest = angle;
    }
  });
  if (delta > toleranceDegrees) return end;
  const radians = (nearest * Math.PI) / 180;
  return {
    x: start.x + Math.cos(radians) * distance,
    y: start.y + Math.sin(radians) * distance,
    snapped: true,
    angle: nearest,
  };
}

export function pointNear(a, b, tolerance = 10) {
  return distanceBetweenPoints(a, b) <= tolerance;
}

export function pointInPolygon(point, polygon = []) {
  if (!point || polygon.length < 3) return false;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i, i += 1) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;
    const intersects = yi > point.y !== yj > point.y && point.x < ((xj - xi) * (point.y - yi)) / ((yj - yi) || 0.000001) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

export function boundsForPoints(points = []) {
  if (!points.length) return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 };
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
}

export function rectToPoints(rect) {
  const rotation = ((rect.rotation || 0) * Math.PI) / 180;
  const hw = rect.width / 2;
  const hh = rect.height / 2;
  const corners = [
    { x: -hw, y: -hh },
    { x: hw, y: -hh },
    { x: hw, y: hh },
    { x: -hw, y: hh },
  ];
  return corners.map((corner) => ({
    x: rect.x + corner.x * Math.cos(rotation) - corner.y * Math.sin(rotation),
    y: rect.y + corner.x * Math.sin(rotation) + corner.y * Math.cos(rotation),
  }));
}

function orientation(a, b, c) {
  const value = (b.y - a.y) * (c.x - b.x) - (b.x - a.x) * (c.y - b.y);
  if (Math.abs(value) < 0.000001) return 0;
  return value > 0 ? 1 : 2;
}

function onSegment(a, b, c) {
  return b.x <= Math.max(a.x, c.x) && b.x >= Math.min(a.x, c.x) && b.y <= Math.max(a.y, c.y) && b.y >= Math.min(a.y, c.y);
}

export function segmentsIntersect(p1, q1, p2, q2) {
  const o1 = orientation(p1, q1, p2);
  const o2 = orientation(p1, q1, q2);
  const o3 = orientation(p2, q2, p1);
  const o4 = orientation(p2, q2, q1);
  if (o1 !== o2 && o3 !== o4) return true;
  if (o1 === 0 && onSegment(p1, p2, q1)) return true;
  if (o2 === 0 && onSegment(p1, q2, q1)) return true;
  if (o3 === 0 && onSegment(p2, p1, q2)) return true;
  if (o4 === 0 && onSegment(p2, q1, q2)) return true;
  return false;
}

export function polygonsIntersect(polyA = [], polyB = []) {
  if (polyA.length < 3 || polyB.length < 3) return false;
  if (polyA.some((p) => pointInPolygon(p, polyB)) || polyB.some((p) => pointInPolygon(p, polyA))) return true;
  for (let i = 0; i < polyA.length; i += 1) {
    for (let j = 0; j < polyB.length; j += 1) {
      if (segmentsIntersect(polyA[i], polyA[(i + 1) % polyA.length], polyB[j], polyB[(j + 1) % polyB.length])) return true;
    }
  }
  return false;
}

export function circleIntersectsPolygon(circle, polygon = []) {
  if (!circle || polygon.length < 3) return false;
  if (pointInPolygon({ x: circle.x, y: circle.y }, polygon)) return true;
  for (let i = 0; i < polygon.length; i += 1) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    if (distancePointToSegment({ x: circle.x, y: circle.y }, a, b) <= circle.radius) return true;
  }
  return false;
}

export function distancePointToSegment(point, a, b) {
  const lengthSquared = Math.pow(distanceBetweenPoints(a, b), 2);
  if (lengthSquared === 0) return distanceBetweenPoints(point, a);
  const t = Math.max(0, Math.min(1, ((point.x - a.x) * (b.x - a.x) + (point.y - a.y) * (b.y - a.y)) / lengthSquared));
  const projection = { x: a.x + t * (b.x - a.x), y: a.y + t * (b.y - a.y) };
  return distanceBetweenPoints(point, projection);
}

export function distancePolygonToPolyline(poly = [], path = []) {
  if (!poly.length || path.length < 2) return Infinity;
  let min = Infinity;
  for (const point of poly) {
    for (let i = 0; i < path.length - 1; i += 1) {
      min = Math.min(min, distancePointToSegment(point, path[i], path[i + 1]));
    }
  }
  return min;
}

export function edgeMidpoint(points = [], index = 0) {
  const a = points[index];
  const b = points[(index + 1) % points.length];
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

export function edgeNormal(points = [], index = 0) {
  const a = points[index];
  const b = points[(index + 1) % points.length];
  const centroid = polygonCentroid(points);
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const length = Math.hypot(dx, dy) || 1;
  const normalA = { x: -dy / length, y: dx / length };
  const mid = edgeMidpoint(points, index);
  const test = { x: mid.x + normalA.x * 12, y: mid.y + normalA.y * 12 };
  const outward = distanceBetweenPoints(test, centroid) > distanceBetweenPoints(mid, centroid) ? normalA : { x: -normalA.x, y: -normalA.y };
  return outward;
}

export function lineIntersection(p1, p2, p3, p4) {
  const x1 = p1.x;
  const y1 = p1.y;
  const x2 = p2.x;
  const y2 = p2.y;
  const x3 = p3.x;
  const y3 = p3.y;
  const x4 = p4.x;
  const y4 = p4.y;
  const denominator = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  if (Math.abs(denominator) < 0.000001) return null;
  const px = ((x1 * y2 - y1 * x2) * (x3 - x4) - (x1 - x2) * (x3 * y4 - y3 * x4)) / denominator;
  const py = ((x1 * y2 - y1 * x2) * (y3 - y4) - (y1 - y2) * (x3 * y4 - y3 * x4)) / denominator;
  const onA = px >= Math.min(x1, x2) - 0.1 && px <= Math.max(x1, x2) + 0.1 && py >= Math.min(y1, y2) - 0.1 && py <= Math.max(y1, y2) + 0.1;
  const onB = px >= Math.min(x3, x4) - 0.1 && px <= Math.max(x3, x4) + 0.1 && py >= Math.min(y3, y4) - 0.1 && py <= Math.max(y3, y4) + 0.1;
  return onA && onB ? { x: px, y: py } : null;
}

export function clipLineToPolygon(anchor, direction, polygon = []) {
  const far = 10000;
  const p1 = { x: anchor.x - direction.x * far, y: anchor.y - direction.y * far };
  const p2 = { x: anchor.x + direction.x * far, y: anchor.y + direction.y * far };
  const hits = [];
  for (let i = 0; i < polygon.length; i += 1) {
    const hit = lineIntersection(p1, p2, polygon[i], polygon[(i + 1) % polygon.length]);
    if (hit && !hits.some((existing) => distanceBetweenPoints(existing, hit) < 0.5)) hits.push(hit);
  }
  if (hits.length < 2) return null;
  hits.sort((a, b) => (a.x - anchor.x) * direction.x + (a.y - anchor.y) * direction.y - ((b.x - anchor.x) * direction.x + (b.y - anchor.y) * direction.y));
  return [hits[0], hits[hits.length - 1]];
}

export function generateRafterSegments(roof, pixelsPerInch) {
  if (!roof || roof.gutterEdgeIndex === null || roof.gutterEdgeIndex === undefined || !roof.points?.length) return [];
  const points = roof.points;
  const edgeIndex = roof.gutterEdgeIndex;
  const a = points[edgeIndex];
  const b = points[(edgeIndex + 1) % points.length];
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const edgeLength = Math.hypot(dx, dy) || 1;
  const tangent = { x: dx / edgeLength, y: dy / edgeLength };
  const normal = edgeNormal(points, edgeIndex);
  const spacingPx = Math.max(4, (roof.rafterSpacing || 24) * pixelsPerInch);
  const bounds = boundsForPoints(points);
  const span = Math.hypot(bounds.width, bounds.height) + 80;
  const center = polygonCentroid(points);
  const segments = [];
  for (let offset = -span; offset <= span; offset += spacingPx) {
    const anchor = { x: center.x + tangent.x * offset, y: center.y + tangent.y * offset };
    const clipped = clipLineToPolygon(anchor, normal, points);
    if (clipped && distanceBetweenPoints(clipped[0], clipped[1]) > 5) segments.push(clipped);
  }
  return segments;
}

export function formatSegmentLength(a, b, pixelsPerInch, formatter) {
  return formatter(pixelsToInches(distanceBetweenPoints(a, b), pixelsPerInch));
}
